/**
 * Campaign id to campaign name, for the inbox.
 *
 * A thread stores only `campaignId`, because the marketing module owns the row
 * and denormalising the name onto every thread would mean a renamed campaign
 * silently disagrees with itself across a few hundred threads. So the inbox
 * looks the name up instead.
 *
 * One request for the whole list rather than one per thread: the campaign list
 * route returns every campaign in a single query with no message bodies, and
 * there are tens of campaigns, not thousands. Loading it once per screen is
 * cheaper than a lookup per row and far cheaper than a join the inbox would
 * otherwise have to make across a module boundary.
 *
 * Failure is silent on purpose. If marketing is unreachable or the operator
 * lacks the permission, the inbox still has to render; it simply shows no
 * campaign names.
 */
import { useEffect, useState } from "react";
import { listCampaigns, type CampaignRow } from "../../../lib/marketing";

export interface CampaignNames {
  /** id -> name, empty until loaded. */
  byId: Record<string, string>;
  /** Every campaign, for a filter dropdown. */
  rows: CampaignRow[];
}

export function useCampaignNames(): CampaignNames {
  const [rows, setRows] = useState<CampaignRow[]>([]);

  useEffect(() => {
    let alive = true;
    listCampaigns()
      .then((res) => {
        if (alive) setRows(res.rows);
      })
      .catch(() => {
        /* the inbox works without campaign names */
      });
    return () => {
      alive = false;
    };
  }, []);

  return {
    rows,
    byId: Object.fromEntries(rows.map((c) => [c.id, c.name])),
  };
}
